<template>
  <app-layout>
    <div class="sc-container">
      <div class="page-wrap">

        <!-- Sidebar -->
        <aside class="page-sidebar">
          <div class="sidebar-card">
            <div class="sidebar-profile">
              <img
                v-if="user.avatar"
                class="sidebar-avatar"
                :src="$avatarSrc(user.avatar, 'users')"
                alt="User avatar"
              />
              <img
                v-else
                class="sidebar-avatar"
                src="/assets/avatar.png"
                alt="User avatar"
              />
              <div class="sidebar-name">{{ user.fname }} {{ user.lname }}</div>
              <div class="sidebar-sub" v-if="user.currentcompany">
                {{ user.current_position }} &mdash; {{ user.currentcompany.name }}
              </div>
              <div class="sidebar-sub" v-else>{{ user.profession }}</div>
            </div>
            <nav class="sidebar-nav">
              <router-link class="sidebar-link" to="/profile/update">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Edit Profile
              </router-link>
              <router-link class="sidebar-link active" to="/profile/change-password">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Change Password
              </router-link>
              <router-link class="sidebar-link" to="/notification-settings">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Notification Settings
              </router-link>
              <router-link class="sidebar-link" to="/my-Projects">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 20h20M4 20V10l8-6 8 6v10"/></svg>
                My Projects
              </router-link>
              <router-link class="sidebar-link" to="/my-followers">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Following
              </router-link>
              <router-link class="sidebar-link" to="/my-blog-history">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                My Blog
              </router-link>
              <router-link class="sidebar-link" to="/my-service-requests">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
                Service Requests
              </router-link>
            </nav>
          </div>
        </aside>

        <!-- Main content -->
        <main class="page-main">
          <div class="main-card">
            <div class="main-card-header">
              <h2 class="main-card-title">Change Password</h2>
              <p class="main-card-subtitle">Update your account password below.</p>
            </div>

            <div class="main-card-body">
              <!-- Alert messages -->
              <div v-if="successMsg" class="alert alert-success">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                {{ successMsg }}
              </div>
              <div v-if="errorMsg" class="alert alert-error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {{ errorMsg }}
              </div>

              <form @submit.prevent="update" novalidate>
                <!-- Current Password -->
                <div class="form-group">
                  <label class="form-label">
                    Current Password <span class="required">*</span>
                  </label>
                  <div class="input-wrap">
                    <input
                      :type="showCurrent ? 'text' : 'password'"
                      v-model="form.current_password"
                      class="form-input"
                      placeholder="Enter your current password"
                      required
                    />
                    <button type="button" class="eye-btn" @click="showCurrent = !showCurrent" aria-label="Toggle password visibility">
                      <svg v-if="!showCurrent" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    </button>
                  </div>
                </div>

                <!-- New Password row -->
                <div class="form-row-2">
                  <div class="form-group">
                    <label class="form-label">
                      New Password <span class="required">*</span>
                    </label>
                    <div class="input-wrap">
                      <input
                        :type="showNew ? 'text' : 'password'"
                        v-model="form.password"
                        class="form-input"
                        placeholder="Enter new password"
                        required
                      />
                      <button type="button" class="eye-btn" @click="showNew = !showNew" aria-label="Toggle password visibility">
                        <svg v-if="!showNew" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      </button>
                    </div>
                  </div>

                  <div class="form-group">
                    <label class="form-label">
                      Confirm New Password <span class="required">*</span>
                    </label>
                    <div class="input-wrap">
                      <input
                        :type="showConfirm ? 'text' : 'password'"
                        v-model="form.password_confirmation"
                        class="form-input"
                        placeholder="Confirm new password"
                        required
                      />
                      <button type="button" class="eye-btn" @click="showConfirm = !showConfirm" aria-label="Toggle password visibility">
                        <svg v-if="!showConfirm" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="form-actions">
                  <router-link to="/" class="btn-outline">Cancel</router-link>
                  <button type="submit" class="btn-primary" :disabled="loading">
                    <span v-if="loading" class="spinner"></span>
                    <span>{{ loading ? 'Saving...' : 'Save Changes' }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>

      </div>
    </div>
  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout.vue';
import axios from 'axios';

export default {
  name: 'ChangePassword',
  components: { AppLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL,
      company: {},
      user: {},
      loading: false,
      id: localStorage.getItem('slug'),
      form: { current_password: '', password: '', password_confirmation: '' },
      successMsg: '',
      errorMsg: '',
      showCurrent: false,
      showNew: false,
      showConfirm: false,
    };
  },

  created() {
    this.fetchCompany();
    this.fetchUser();
  },

  methods: {
    logout() {
      localStorage.clear();
      this.$router.push({ name: 'Login' });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data; })
        .catch(() => {});
    },

    fetchUser() {
      axios(process.env.VUE_APP_API_URL + 'user/details/' + localStorage.getItem('user'))
        .then(res => { if (res.status === 200) this.user = res.data; })
        .catch(() => {});
    },

    update() {
      this.loading = true;
      this.errorMsg = '';
      this.successMsg = '';
      const fd = new FormData();
      fd.append('current_password', this.form.current_password);
      fd.append('password', this.form.password);
      fd.append('password_confirmation', this.form.password_confirmation);
      axios.post(process.env.VUE_APP_API_URL + 'user/change-password/' + localStorage.getItem('user'), fd)
        .then(res => {
          if (res.status === 200) {
            this.successMsg = res.message || 'Password updated successfully.';
            this.form = { current_password: '', password: '', password_confirmation: '' };
          } else {
            this.errorMsg = res.message || 'Could not update password.';
          }
        })
        .catch(() => { this.errorMsg = 'Could not update password. Please try again.'; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  padding: 2.5rem 0;
}

/* ── Sidebar ── */
.page-sidebar {
  position: sticky;
  top: 80px;
  align-self: start;
}

.sidebar-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.sidebar-profile {
  padding: 1.5rem 1rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
}

.sidebar-avatar {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  object-fit: cover;
  border: 3px solid var(--border);
}

.sidebar-name {
  margin-top: 0.75rem;
  font-weight: 700;
  color: var(--text);
  font-size: 0.95rem;
}

.sidebar-sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.sidebar-nav {
  padding: 0.5rem 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.875rem;
  color: var(--text-muted);
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.sidebar-link:hover,
.sidebar-link.active {
  background: var(--surface-2);
  color: var(--primary);
}

.sidebar-link.active {
  font-weight: 600;
  border-right: 3px solid var(--accent);
}

.sidebar-link svg {
  flex-shrink: 0;
}

/* ── Main card ── */
.page-main {
  min-width: 0;
}

.main-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.main-card-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid var(--border);
}

.main-card-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 0.25rem;
}

.main-card-subtitle {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
}

.main-card-body {
  padding: 1.75rem;
}

/* ── Alerts ── */
.alert {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.875rem 1rem;
  border-radius: var(--radius);
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
}

.alert-success {
  background: #ecfdf5;
  color: var(--success);
  border: 1px solid #a7f3d0;
}

.alert-error {
  background: #fef2f2;
  color: var(--danger);
  border: 1px solid #fecaca;
}

/* ── Form ── */
.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0.45rem;
}

.required {
  color: var(--danger);
}

.input-wrap {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 0.65rem 2.75rem 0.65rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  font-size: 0.9rem;
  color: var(--text);
  background: var(--surface);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.08);
}

.eye-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  padding: 0;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}

.eye-btn:hover {
  color: var(--primary);
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border-light);
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-right: 0.4rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .page-wrap {
    grid-template-columns: 1fr;
    padding: 1.5rem 0;
  }

  .page-sidebar {
    position: static;
  }

  .sidebar-nav {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.5rem;
  }

  .sidebar-link {
    padding: 0.5rem 0.75rem;
    border-radius: var(--radius);
    font-size: 0.8rem;
  }

  .sidebar-link.active {
    border-right: none;
    background: var(--surface-3);
  }

  .form-row-2 {
    grid-template-columns: 1fr;
  }
}
</style>
